import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs with UTC and timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Centralized date utility functions for consistent UTC storage and local display
 * 
 * PRINCIPLE: Always store dates in UTC, always display dates in local time
 */

/**
 * Convert a date to UTC for storage in the database
 * @param {Date|string|dayjs} date - The date to convert to UTC
 * @returns {Date} - UTC date for database storage
 */
export const toUtcForStorage = (date) => {
    if (!date) return null;
    // For current date/time, we want to store the actual UTC time
    // Simply convert the local time to UTC without adding offset
    return dayjs(date).utc().toDate();
};

/**
 * Convert a local date/time to UTC for storage, preserving the local time values
 * This is specifically for handling user input where they enter a local time
 * @param {Date|string|dayjs} localDate - The local date to convert to UTC
 * @returns {Date} - UTC date for database storage
 */
export const localToUtcForStorage = (localDate) => {
    if (!localDate) return null;
    // Parse the date as local time, then convert to UTC
    // This ensures the time values are preserved correctly
    const local = dayjs(localDate);
    return local.utc().toDate();
};

/**
 * Convert a date from DateTimePicker to UTC for storage
 * This handles the specific case where DateTimePicker gives us a local time
 * @param {Date|string|dayjs} pickerDate - The date from DateTimePicker
 * @returns {Date} - UTC date for database storage
 */
export const dateTimePickerToUtc = (pickerDate) => {
    if (!pickerDate) return null;
    // DateTimePicker gives us a local time, we need to convert it to UTC
    // Simply use dayjs to convert local to UTC (this handles the timezone offset correctly)
    return dayjs(pickerDate).utc().toDate();
};

/**
 * Convert a UTC date from the database to local time for display
 * @param {Date|string|dayjs} utcDate - The UTC date from the database
 * @returns {dayjs} - Local time dayjs object for display
 */
export const fromUtcForDisplay = (utcDate) => {
    if (!utcDate) return dayjs();
    return dayjs.utc(utcDate).local();
};

/**
 * Get current date/time in local time for new entries
 * @returns {dayjs} - Current local time
 */
export const getCurrentLocalTime = () => {
    return dayjs();
};

/**
 * Format a date for display in the UI
 * @param {Date|string|dayjs} date - The date to format
 * @param {string} format - The format string (default: 'MM/DD/YYYY HH:mm')
 * @returns {string} - Formatted date string
 */
export const formatForDisplay = (date, format = 'MM/DD/YYYY HH:mm') => {
    if (!date) return '';
    return dayjs(date).format(format);
};

/**
 * Format a UTC date for display in local time with standard format
 * @param {Date|string|dayjs} utcDate - The UTC date from the database
 * @returns {string} - Formatted date string in local time (YYYY-MM-DD - HH:mm AM/PM)
 */
export const formatUtcForDisplay = (utcDate) => {
    if (!utcDate) return '';
    return fromUtcForDisplay(utcDate).format('YYYY-MM-DD - hh:mm A');
};

/**
 * Create a dayjs object from a date for use in date pickers
 * @param {Date|string|dayjs} date - The date to convert
 * @returns {dayjs} - Dayjs object for date pickers
 */
export const createDayjsForPicker = (date) => {
    if (!date) return dayjs();
    return dayjs(date);
};

/**
 * Convert a dayjs object to a Date for API calls
 * @param {dayjs} dayjsObj - The dayjs object
 * @returns {Date} - Date object for API
 */
export const dayjsToDate = (dayjsObj) => {
    if (!dayjsObj) return new Date();
    return dayjsObj.toDate();
};

/**
 * Check if a date is valid
 * @param {any} date - The date to check
 * @returns {boolean} - True if valid date
 */
export const isValidDate = (date) => {
    if (!date) return false;
    return dayjs(date).isValid();
};

/**
 * Get the user's timezone
 * @returns {string} - User's timezone
 */
export const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Convert a date to the user's local timezone
 * @param {Date|string|dayjs} date - The date to convert
 * @returns {dayjs} - Date in user's local timezone
 */
export const toUserTimezone = (date) => {
    if (!date) return dayjs();
    return dayjs(date).tz(getUserTimezone());
};

/**
 * Convert a date from user's local timezone to UTC
 * @param {Date|string|dayjs} date - The date to convert
 * @returns {dayjs} - Date in UTC
 */
export const fromUserTimezoneToUtc = (date) => {
    if (!date) return dayjs().utc();
    return dayjs(date).utc();
};

